import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { errorMessage, interviewProcessApi } from '@/lib/api';
import type {
  CandidateProgress,
  InterviewProcess,
  InterviewReportRow,
  InterviewStats,
  Invitation,
  Paginated,
} from '@/types';

export interface InterviewState {
  processes: InterviewProcess[];
  currentProcess: InterviewProcess | null;
  invitations: Invitation[];
  invitationPagination: Paginated<Invitation>['pagination'] | null;
  progress: CandidateProgress | null;
  reports: InterviewReportRow[];
  reportPagination: Paginated<InterviewReportRow>['pagination'] | null;
  stats: InterviewStats | null;
  /** The link from the most recent invitation — shown once so HR can copy it. */
  lastIssuedLink: string | null;
  loading: boolean;
  saving: boolean;
  error: string | null;
}

const initialState: InterviewState = {
  processes: [],
  currentProcess: null,
  invitations: [],
  invitationPagination: null,
  progress: null,
  reports: [],
  reportPagination: null,
  stats: null,
  lastIssuedLink: null,
  loading: false,
  saving: false,
  error: null,
};

export const fetchProcesses = createAsyncThunk<InterviewProcess[], void, { rejectValue: string }>(
  'interviews/fetchProcesses',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await interviewProcessApi.get<{ data: InterviewProcess[] }>('/interview-processes');
      return data.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load interview processes'));
    }
  }
);

export const fetchProcessForJob = createAsyncThunk<InterviewProcess | null, string, { rejectValue: string }>(
  'interviews/fetchProcessForJob',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await interviewProcessApi.get<InterviewProcess>(`/interview-processes/job/${jobId}`);
      return data;
    } catch (err) {
      // "Not configured yet" is the normal starting state for a new job.
      if ((err as { status?: number }).status === 404) return null;
      return rejectWithValue(errorMessage(err, 'Could not load the interview process'));
    }
  }
);

export const saveProcess = createAsyncThunk<InterviewProcess, Record<string, unknown>, { rejectValue: string }>(
  'interviews/saveProcess',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await interviewProcessApi.post<InterviewProcess>('/interview-processes', payload);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not save the interview process'));
    }
  }
);

export const fetchInvitations = createAsyncThunk<
  Paginated<Invitation>,
  Record<string, unknown> | void,
  { rejectValue: string }
>('interviews/fetchInvitations', async (params, { rejectWithValue }) => {
  try {
    const { data } = await interviewProcessApi.get<Paginated<Invitation>>('/invitations', {
      params: params ?? undefined,
    });
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not load invitations'));
  }
});

export const createInvitation = createAsyncThunk<Invitation, Record<string, unknown>, { rejectValue: string }>(
  'interviews/createInvitation',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await interviewProcessApi.post<Invitation>('/invitations', payload);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not send the invitation'));
    }
  }
);

export const cancelInvitation = createAsyncThunk<string, string, { rejectValue: string }>(
  'interviews/cancelInvitation',
  async (invitationId, { rejectWithValue }) => {
    try {
      await interviewProcessApi.post(`/invitations/${invitationId}/cancel`);
      return invitationId;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not cancel the invitation'));
    }
  }
);

export const fetchProgress = createAsyncThunk<CandidateProgress | null, string, { rejectValue: string }>(
  'interviews/fetchProgress',
  async (applicationId, { rejectWithValue }) => {
    try {
      const { data } = await interviewProcessApi.get<CandidateProgress>(`/progress/${applicationId}`);
      return data;
    } catch (err) {
      if ((err as { status?: number }).status === 404) return null;
      return rejectWithValue(errorMessage(err, 'Could not load interview progress'));
    }
  }
);

export const startProcessForCandidate = createAsyncThunk<
  { progress: CandidateProgress['rounds']; invited: { roundNumber: number; interviewUrl: string | null } | null },
  { applicationId: string; jobId: string; candidateEmail: string; candidateName?: string; candidateId?: string; invite: boolean },
  { rejectValue: string }
>('interviews/startProcess', async ({ applicationId, ...body }, { rejectWithValue }) => {
  try {
    const { data } = await interviewProcessApi.post(`/progress/${applicationId}/initialize`, body);
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not start the interview process'));
  }
});

/**
 * Opens the candidate's next round.
 *
 * The action behind "release each round yourself": a process configured that way parks a
 * candidate who has cleared a round until somebody does this, which issues the invitation
 * and emails them the link.
 */
export const releaseNextRound = createAsyncThunk<
  { roundNumber: number; requiresBooking: boolean } | null,
  { applicationId: string; jobId: string; candidateEmail: string; candidateName?: string | null; candidateId?: string | null },
  { rejectValue: string }
>('interviews/releaseNextRound', async ({ applicationId, ...candidate }, { rejectWithValue }) => {
  try {
    const { data } = await interviewProcessApi.post<{
      invited: { roundNumber: number; requiresBooking: boolean } | null;
    }>(`/progress/${applicationId}/invite-next`, candidate);
    return data.invited;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not open the next round'));
  }
});

export const rejectFromProcess = createAsyncThunk<
  string,
  { applicationId: string; reason?: string },
  { rejectValue: string }
>('interviews/reject', async ({ applicationId, reason }, { rejectWithValue }) => {
  try {
    await interviewProcessApi.post(`/progress/${applicationId}/reject`, { reason });
    return applicationId;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not reject this candidate'));
  }
});

/**
 * The final decision on an application.
 *
 * Returns whether the candidate was actually emailed, rather than a bare success, so the
 * screen can say which of the two things happened. Telling a recruiter the candidate has
 * been told when no mail went out is the one outcome worth never showing.
 */
export const decideCandidate = createAsyncThunk<
  { emailSent: boolean; emailError?: string },
  { applicationId: string; outcome: 'selected' | 'rejected'; note?: string },
  { rejectValue: string }
>('interviews/decide', async ({ applicationId, outcome, note }, { rejectWithValue }) => {
  try {
    const { data } = await interviewProcessApi.post<{ emailSent: boolean; emailError?: string }>(
      `/progress/${applicationId}/decision`,
      { outcome, note }
    );
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not record the decision'));
  }
});

export const fetchReports = createAsyncThunk<
  Paginated<InterviewReportRow>,
  Record<string, unknown> | void,
  { rejectValue: string }
>('interviews/fetchReports', async (params, { rejectWithValue }) => {
  try {
    const { data } = await interviewProcessApi.get<Paginated<InterviewReportRow>>('/company/interview-reports', {
      params: params ?? undefined,
    });
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not load interview reports'));
  }
});

export const fetchInterviewStats = createAsyncThunk<InterviewStats, void, { rejectValue: string }>(
  'interviews/stats',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await interviewProcessApi.get<InterviewStats>('/company/interview-stats');
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load interview statistics'));
    }
  }
);

const interviewSlice = createSlice({
  name: 'interviews',
  initialState,
  reducers: {
    clearIssuedLink(state) {
      state.lastIssuedLink = null;
    },
    clearCurrentProcess(state) {
      state.currentProcess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProcesses.fulfilled, (state, action) => {
        state.processes = action.payload;
      })
      .addCase(fetchProcessForJob.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProcessForJob.fulfilled, (state, action) => {
        state.loading = false;
        state.currentProcess = action.payload;
      })
      .addCase(fetchProcessForJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(saveProcess.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(saveProcess.fulfilled, (state, action) => {
        state.saving = false;
        state.currentProcess = action.payload;
      })
      .addCase(saveProcess.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchInvitations.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchInvitations.fulfilled, (state, action) => {
        state.loading = false;
        state.invitations = action.payload.data;
        state.invitationPagination = action.payload.pagination;
      })
      .addCase(fetchInvitations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(createInvitation.fulfilled, (state, action) => {
        state.invitations.unshift(action.payload);
        state.lastIssuedLink = action.payload.interviewUrl ?? null;
      })
      .addCase(cancelInvitation.fulfilled, (state, action) => {
        const invitation = state.invitations.find((i) => i.id === action.payload);
        if (invitation) invitation.status = 'cancelled';
      })
      .addCase(fetchProgress.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProgress.fulfilled, (state, action) => {
        state.loading = false;
        state.progress = action.payload;
      })
      .addCase(fetchProgress.rejected, (state) => {
        state.loading = false;
      })
      .addCase(startProcessForCandidate.pending, (state) => {
        state.saving = true;
      })
      .addCase(startProcessForCandidate.fulfilled, (state, action) => {
        state.saving = false;
        state.lastIssuedLink = action.payload.invited?.interviewUrl ?? null;
      })
      .addCase(startProcessForCandidate.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchReports.fulfilled, (state, action) => {
        state.loading = false;
        state.reports = action.payload.data;
        state.reportPagination = action.payload.pagination;
      })
      .addCase(fetchReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchInterviewStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  },
});

export const { clearIssuedLink, clearCurrentProcess } = interviewSlice.actions;
export default interviewSlice.reducer;
